import { ArrowRightIcon, CheckIcon, ShieldCheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { ProductVideo } from '../components/marketing/ProductVideo';
import { Cluster, Grid, Inline, Split, Stack } from '../components/ui';
import { marketingMedia } from '../lib/marketing';

const mediaById = Object.fromEntries(marketingMedia.map((media) => [media.id, media]));

function media(id: string) {
  const item = mediaById[id];
  if (!item) throw new Error(`Missing marketing media: ${id}`);
  return item;
}

const controlPoints = [
  'Your team approves important AI actions',
  'Clear roles keep the right people in control',
  'New versions never disrupt work already underway',
];

export function LandingPage(): JSX.Element {
  return (
    <div className="space-y-28 sm:space-y-36">
      <section className="glass-card relative overflow-hidden px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16">
        <div
          className="absolute -right-24 -top-32 h-96 w-96 rounded-full bg-[radial-gradient(circle_at_center,hsl(278_94%_74%/.40),transparent_68%)] blur-2xl dark:bg-[radial-gradient(circle_at_center,hsl(212_92%_62%/.34),transparent_68%)]"
          aria-hidden
        />
        <div
          className="absolute -bottom-40 -left-28 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle_at_center,hsl(189_88%_68%/.30),transparent_68%)] blur-2xl dark:bg-[radial-gradient(circle_at_center,hsl(286_86%_66%/.24),transparent_68%)]"
          aria-hidden
        />

        <Split
          ratio="0.88:1.12"
          gap={12}
          align="center"
          className="relative"
          primary={
            <div className="space-y-7">
              <span className="badge-soft">
                <SparklesIcon className="h-4 w-4" /> Built for real operations
              </span>
              <div className="space-y-5">
                <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-[3.55rem] lg:leading-[1.02]">
                  Turn requests into working workflows—not another backlog.
                </h1>
                <p className="text-lg text-muted-foreground">
                  DefectSnap brings form building, team access, safe updates, and AI approvals into one product. AI
                  prepares the work; your people review, test, and launch it.
                </p>
              </div>
              <Cluster gap={3}>
                <Link to="/signup" className="btn-glass btn-primary focus-ring text-sm">
                  Build your first workflow <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <a href="#product-tour" className="btn-glass btn-secondary focus-ring text-sm">
                  See the real product
                </a>
              </Cluster>
              <Grid columns={{ base: 1, sm: 3, lg: 1, xl: 3 }} gap={2} className="text-sm text-muted-foreground">
                {['Real product', 'You stay in control', 'Safe updates'].map((proof) => (
                  <Inline gap={2} key={proof}>
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-success/10 text-success">
                      <CheckIcon className="h-3.5 w-3.5" />
                    </span>
                    {proof}
                  </Inline>
                ))}
              </Grid>
            </div>
          }
          secondary={<ProductVideo media={media('form-builder')} eager />}
        />
      </section>

      <section id="product-tour" className="scroll-mt-28 space-y-10">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <span className="badge-soft">From intent to testable draft</span>
          <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
            Give every request a clear path into the product.
          </h2>
          <p className="text-muted-foreground">
            Start from a ready-made building block, an existing document, a blank canvas, or simply tell the AI what
            you need. Every path produces something your team can review before it goes live.
          </p>
        </div>

        <ProductVideo media={media('template-creation')} />

        <Grid as="ol" columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
          {[
            ['01', 'Describe', 'Capture the outcome, evidence, and reviewer.'],
            ['02', 'Draft', 'Assemble sections and response types in the editor.'],
            ['03', 'Test', 'Preview the experience before anyone uses it.'],
            ['04', 'Publish', 'Create a stable version for the organization.'],
          ].map(([number, title, description]) => (
            <li key={number} className="surface-panel p-5">
              <span className="text-xs font-semibold tracking-[0.2em] text-primary">{number}</span>
              <h3 className="mt-3 text-base font-semibold text-foreground">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{description}</p>
            </li>
          ))}
        </Grid>
      </section>

      <section className="space-y-10">
        <Split
          ratio="0.75:1.25"
          gap={8}
          align="end"
          primary={
            <div className="space-y-4">
              <span className="badge-soft">
                <ShieldCheckIcon className="h-4 w-4" /> Made for growing teams
              </span>
              <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
                Powerful enough to change the app. Controlled enough for enterprise.
              </h2>
            </div>
          }
          secondary={
            <Stack gap={3}>
              {controlPoints.map((point) => (
                <Inline gap={3} key={point} className="glass-card px-5 py-4 text-sm text-foreground">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <CheckIcon className="h-4 w-4" />
                  </span>
                  {point}
                </Inline>
              ))}
            </Stack>
          }
        />

        <Grid as="div" columns={{ base: 1, lg: 2 }} gap={10}>
          {[media('access-and-roles'), media('governed-agent-plan')].map((item) => (
            <article key={item.id} className="space-y-5">
              <ProductVideo media={item} />
              <div className="px-2">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{item.eyebrow}</p>
                <h3 className="mt-2 text-2xl font-semibold text-foreground">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
              </div>
            </article>
          ))}
        </Grid>
      </section>

      <section className="space-y-12">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <span className="badge-soft">Safe updates, clear history</span>
          <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
            Improve quickly without disrupting live work.
          </h2>
          <p className="text-muted-foreground">
            Safe version history protects inspections already underway, while the workflow continues from field
            evidence to a finished report.
          </p>
        </div>

        {[media('template-versioning'), media('inspection-to-report')].map((item, index) => (
          <Grid as="article" columns={{ base: 1, lg: 2 }} gap={8} className="lg:items-center" key={item.id}>
            <div className={index % 2 ? 'lg:order-2' : ''}>
              <ProductVideo media={item} />
            </div>
            <div className={`space-y-4 px-2 sm:px-6 ${index % 2 ? 'lg:order-1' : ''}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{item.eyebrow}</p>
              <h3 className="text-3xl font-semibold text-foreground">{item.title}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </div>
          </Grid>
        ))}
      </section>

      <section className="glass-card px-6 py-10 sm:px-10 lg:px-12">
        <Split
          ratio="0.82:1.18"
          gap={10}
          align="center"
          primary={
            <div className="space-y-5">
              <span className="badge-soft">Mobile field experience</span>
              <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
                The same operating model, in the field.
              </h2>
              <p className="text-muted-foreground">
                DefectSnap works beautifully on smaller screens, keeping inspection details, evidence actions, and
                connection status easy to use in the field.
              </p>
              <p className="rounded-2xl border border-border/60 bg-background/60 px-4 py-3 text-xs text-muted-foreground">
                Available now: mobile-friendly and installable. Coming next: a full offline field mode with safe
                syncing when the connection returns.
              </p>
            </div>
          }
          secondary={<ProductVideo media={media('mobile-field-experience')} />}
        />
      </section>

      <Stack as="section" gap={6} align="center" className="glass-card relative overflow-hidden px-8 py-14 text-center">
        <div
          className="absolute inset-x-[20%] -top-32 h-72 rounded-full bg-[radial-gradient(circle_at_center,hsl(237_86%_72%/.30),transparent_65%)] blur-2xl"
          aria-hidden
        />
        <span className="badge-soft relative">Start with one workflow</span>
        <h2 className="relative max-w-3xl text-3xl font-semibold text-foreground sm:text-4xl">
          Let the backlog describe the need. Let DefectSnap turn it into something your team can use.
        </h2>
        <p className="relative max-w-2xl text-sm text-muted-foreground">
          Build a draft, test the real experience, approve the actions, and publish for the organization—inside the
          system your team already uses.
        </p>
        <Cluster justify="center" gap={3} className="relative">
          <Link to="/signup" className="btn-glass btn-primary focus-ring text-sm">
            Build your first workflow <ArrowRightIcon className="h-4 w-4" />
          </Link>
          <Link to="/security" className="btn-glass btn-secondary focus-ring text-sm">
            See how DefectSnap protects your work
          </Link>
        </Cluster>
      </Stack>
    </div>
  );
}
